import React, { FC } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import ActionButtons from '../components/ActionButtons';
import {
	Orientation,
	DeviceType,
	WriteDirection,
	RangeSettings,
	Platform,
	PopOver,
} from 'sofa-reborn-react';
import { animateCameraRotation, selectSceneItem } from '../store/scene';
import {
	setShowFunctionSleep,
	setShowFunctionStorage,
	setShowMeasurements,
	setShowSelectAll,
	setRotationButtonClicked,
} from '../store/actionButtons';
import { hidePopOver } from '../store/popOver/actions';

const onFunctionSleepClick = (
	dispatch: AppDispatch,
	disableShowFunctionSleep: boolean,
	showFunctionSleep: boolean,
	noValidConnection: PopOver,
	placementInformation: PopOver
): void => {
	if (noValidConnection.showPopOver) {
		dispatch(hidePopOver({ type: 'no-valid-connection' }));
	}
	if (placementInformation.showPopOver) {
		dispatch(hidePopOver({ type: 'placement-information' }));
	}
	if (!disableShowFunctionSleep) {
		dispatch(setShowFunctionSleep(!showFunctionSleep, true));
	}
};

const onFunctionStorageClick = (
	dispatch: AppDispatch,
	disableShowFunctionStorage: boolean,
	showFunctionStorage: boolean,
	noValidConnection: PopOver,
	placementInformation: PopOver
): void => {
	if (noValidConnection.showPopOver) {
		dispatch(hidePopOver({ type: 'no-valid-connection' }));
	}
	if (placementInformation.showPopOver) {
		dispatch(hidePopOver({ type: 'placement-information' }));
	}
	if (!disableShowFunctionStorage) {
		dispatch(setShowFunctionStorage(!showFunctionStorage, true));
	}
};

const onMeasurementsClick = (
	dispatch: AppDispatch,
	disableShowMeasurements: boolean,
	showMeasurements: boolean,
	noValidConnection: PopOver,
	placementInformation: PopOver
): void => {
	if (noValidConnection.showPopOver) {
		dispatch(hidePopOver({ type: 'no-valid-connection' }));
	}
	if (placementInformation.showPopOver) {
		dispatch(hidePopOver({ type: 'placement-information' }));
	}
	if (!disableShowMeasurements) {
		dispatch(setShowMeasurements(!showMeasurements, true));
	}
};

const onRotationClick = (
	dispatch: AppDispatch,
	cameraRotation: number,
	noValidConnection: PopOver,
	placementInformation: PopOver
): void => {
	if (noValidConnection.showPopOver) {
		dispatch(hidePopOver({ type: 'no-valid-connection' }));
	}
	if (placementInformation.showPopOver) {
		dispatch(hidePopOver({ type: 'placement-information' }));
	}
	dispatch(setRotationButtonClicked(true));
	dispatch(animateCameraRotation(cameraRotation));
};

const onSelectAllClick = (
	dispatch: AppDispatch,
	disableShowSelectAll: boolean,
	showSelectAll: boolean
): void => {
	if (!disableShowSelectAll) {
		dispatch(selectSceneItem(null));
		// setTimeout to solve issue with kompis popover-balloon position
		setTimeout(() => {
			dispatch(setShowSelectAll(!showSelectAll));
		}, 0);
	}
};

const ActionButtonsContainer: FC = () => {
	const orientation = useSelector<RootState, Orientation>((state) => state.deviceInfo.orientation);
	const deviceType = useSelector<RootState, DeviceType>((state) => state.deviceInfo.deviceType);
	const writeDirection = useSelector<RootState, WriteDirection>(
		(state) => state.localization.writeDirection
	);
	const platform = useSelector<RootState, Platform>((state) => state.deviceInfo.platform);

	const showFunctionSleep = useSelector<RootState, boolean>(
		(state) => state.actionButtons.showFunctionSleep as boolean
	);
	const showFunctionStorage = useSelector<RootState, boolean>(
		(state) => state.actionButtons.showFunctionStorage as boolean
	);
	const disableShowFunctionSleep = useSelector<RootState, boolean>(
		(state) => state.actionButtons.disableShowFunctionSleep as boolean
	);
	const disableShowFunctionStorage = useSelector<RootState, boolean>(
		(state) => state.actionButtons.disableShowFunctionStorage as boolean
	);
	const disableShowFunctions = useSelector<RootState, boolean>(
		(state) => state.actionButtons.disableShowFunctions as boolean
	);
	const showMeasurements = useSelector<RootState, boolean>(
		(state) => state.actionButtons.showMeasurements as boolean
	);
	const disableShowMeasurements = useSelector<RootState, boolean>(
		(state) => state.actionButtons.disableShowMeasurements as boolean
	);
	const showSelectAll = useSelector<RootState, boolean>(
		(state) => state.actionButtons.showSelectAll as boolean
	);
	const disableShowSelectAll = useSelector<RootState, boolean>(
		(state) => state.actionButtons.disableShowSelectAll as boolean
	);
	const rangeSettings = useSelector<RootState, RangeSettings>(
		(state) => state.settings.rangeSettings as RangeSettings
	);
	const noValidConnection = useSelector<RootState, PopOver>(
		(state) => state.popOver.noValidConnection
	);
	const showPlacementInformation = useSelector<RootState, PopOver>(
		(state) => state.popOver.placementInformation
	);

	const cameraRotation = 1.5708;
	const dispatch = useDispatch<AppDispatch>();

	return (
		<ActionButtons
			orientation={orientation}
			deviceType={deviceType}
			platform={platform}
			writeDirection={writeDirection}
			showFunctionSleep={showFunctionSleep}
			showFunctionStorage={showFunctionStorage}
			showFunctionsButtonSleep={
				rangeSettings.showFunctionsButton || rangeSettings.showFunctionsButtonSleep
			}
			showFunctionsButtonStorage={
				rangeSettings.showFunctionsButton || rangeSettings.showFunctionsButtonStorage
			}
			showMeasurements={showMeasurements}
			showSelectAll={showSelectAll}
			disableShowFunctionSleep={disableShowFunctionSleep}
			disableShowFunctionStorage={disableShowFunctionStorage}
			disableShowFunctions={disableShowFunctions}
			disableShowMeasurements={disableShowMeasurements}
			disableShowSelectAll={disableShowSelectAll}
			disableRotateButton={disableShowMeasurements}
			showSelectAllButton={!rangeSettings.shouldColorAllModules}
			onFunctionSleepClick={(): void =>
				onFunctionSleepClick(
					dispatch,
					disableShowFunctionSleep,
					showFunctionSleep,
					noValidConnection,
					showPlacementInformation
				)
			}
			onFunctionStorageClick={(): void =>
				onFunctionStorageClick(
					dispatch,
					disableShowFunctionStorage,
					showFunctionStorage,
					noValidConnection,
					showPlacementInformation
				)
			}
			onMeasurementsClick={(): void =>
				onMeasurementsClick(
					dispatch,
					disableShowMeasurements,
					showMeasurements,
					noValidConnection,
					showPlacementInformation
				)
			}
			onRotationClick={(): void =>
				onRotationClick(dispatch, cameraRotation, noValidConnection, showPlacementInformation)
			}
			onSelectAllClick={(): void => onSelectAllClick(dispatch, disableShowSelectAll, showSelectAll)}
		></ActionButtons>
	);
};
export default ActionButtonsContainer;
